import memoize from 'lru-memoize';
import React, {DragEvent, FC, PropsWithChildren, useEffect, useMemo} from 'react';
import {DragPreviewOptions, useDrag} from 'react-dnd';

import {supportsDownloadDragSource} from '../../../../libs/shared/helpers/src/environment/platformHelpers';
import {sanitizeFilename} from '../../../../libs/shared/helpers/src/utilities/stringHelpers';
import conversationDragAndDrop1 from '../../../assets/images/conversationDragAndDrop1.svg';
import conversationDragAndDrop2 from '../../../assets/images/conversationDragAndDrop2.svg';
import conversationDragAndDrop3 from '../../../assets/images/conversationDragAndDrop3.svg';
import {
  CellsConversationsDragItem,
  CellsConversationsDragType,
} from '../../../helpers/conversationCellHelpers';
import {addDownloadToDragEvent, addTextToDragEvent} from '../../../helpers/dragAndDropHelpers';
import {makeConversationActionContext} from '../../../helpers/models/conversationHelpers';
import {useAppSelector} from '../../../helpers/reduxHelpers';
import {ConversationBaseModel} from '../../../models/api/conversationModel';
import {MessageTypesEnum} from '../../../models/api/messageModel';
import {buildPublicId} from '../../../models/applications/publicIdTypes';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../services/tracking';
import {getCompanyUrl} from '../../../store/data/company/companySelectors';
import {CellsContext} from '../../../store/panels/cells/context/cellsContext';
import {
  addNativeAttachmentsToDragEvent,
  DraggedAttachmentItemTypesEnum,
} from '../../common/attachments/attachmentsDragAndDrop';

/*
 * Props.
 */

interface ConversationCellDragSourceProps {
  cellsContext: CellsContext;
  sourceCustomViewId: number | undefined;
  conversation: ConversationBaseModel;

  customDragItem: CellsConversationsDragItem | undefined;
}

/*
 * Component.
 */

export const ConversationCellDragSource: FC<PropsWithChildren<ConversationCellDragSourceProps>> = (props) => {
  const {cellsContext, sourceCustomViewId, conversation, customDragItem} = props;
  const companyUrl = useAppSelector(getCompanyUrl);

  const downloadOptions = useMemo(
    () =>
      conversation.messageType === MessageTypesEnum.EMAIL && {
        name: conversationEmlFileName(conversation.subject, conversation.id),
        url: `${location.origin}${companyUrl}/conversations/${conversation.id}/eml?v=${Date.now()}`,
      },
    [companyUrl, conversation.messageType, conversation.subject, conversation.id],
  );

  const conversationActionContext = makeConversationActionContext(cellsContext, conversation);
  const dragItem: CellsConversationsDragItem = useMemo(
    () =>
      customDragItem ?? {
        type: CellsConversationsDragType,
        conversationIds: [conversation.id],
        sourceCustomViewId,
        cellsContext,
        findConversationActionContext: () => conversationActionContext,
        findConversationNamespace: () => conversation.namespace,
        findConversationInboxIds: () => conversation.inboxIds,
        findConversationSubject: () => conversation.subject,
        findConversationMessageType: () => conversation.messageType,
      },
    [
      cellsContext,
      sourceCustomViewId,
      conversation.id,
      conversationActionContext,
      conversation.namespace,
      conversation.inboxIds,
      conversation.subject,
      conversation.messageType,
      customDragItem,
    ],
  );

  const text = dragItem.conversationIds.map((id) => buildPublicId('conversation', id)).join('\n');

  const dragSpec = useMemo(() => ({type: CellsConversationsDragType, item: dragItem}), [dragItem]);
  const [, drag, preview] = useDrag(dragSpec);

  const dragConversationsCount = dragItem.conversationIds.length;
  useEffect(() => {
    const previewOptions: DragPreviewOptions = {
      offsetX: 10,
      offsetY: 10,
    };

    const previewImage = new Image();
    previewImage.src = buildPreviewImage(dragConversationsCount);
    preview(previewImage, previewOptions);
  }, [dragConversationsCount, preview]);

  const onDragEnd = (e: DragEvent) => {
    if (!e.dataTransfer || e.dataTransfer.dropEffect !== 'copy') {
      return;
    }

    trackInterfaceEvent(InterfaceEventsEnum.CONVERSATION_CELL_DRAG_AND_DROP_TO_EML);
  };

  const onDragStart = (e: DragEvent) => {
    if (downloadOptions && supportsDownloadDragSource()) {
      addDownloadToDragEvent(e, downloadOptions);
    }

    addNativeAttachmentsToDragEvent(e, {
      type: DraggedAttachmentItemTypesEnum.CONVERSATIONS,
      conversations: dragItem.conversationIds
        .filter((id) => dragItem.findConversationMessageType(id) === MessageTypesEnum.EMAIL)
        .map((id) => ({
          conversationId: id,
          emlFileName: conversationEmlFileName(dragItem.findConversationSubject(id), id),
        })),
    });

    addTextToDragEvent(e, text);
  };

  return (
    <div ref={(el) => drag(el)} onDragEnd={onDragEnd} onDragStart={onDragStart}>
      {props.children}
    </div>
  );
};

/*
 * Preview image.
 */

const buildPreviewImage = memoize(20)((count: number) => {
  // Find the base image to use.
  const baseImage = findBasePreviewImage(count);

  // If the count is 1, use the image as-is.
  if (count === 1) {
    return baseImage;
  }

  // Otherwise, update it to show the count.
  const actualCount = count > 99 ? 99 : count;
  const decodedImage = decodeSvgImage(baseImage);
  const updatedImage = decodedImage.replace('>X<', `>${actualCount}<`);

  // Re-encode the image and return it.
  return encodeSvgImage(updatedImage);
});

function findBasePreviewImage(count: number): string {
  switch (count) {
    case 1:
      return conversationDragAndDrop1;

    case 2:
      return conversationDragAndDrop2;

    default:
      return conversationDragAndDrop3;
  }
}

function decodeSvgImage(image: string) {
  return atob(image.slice(image.indexOf(',') + 1));
}

function encodeSvgImage(image: string) {
  return `data:image/svg+xml;base64,${btoa(image)}`;
}

function conversationEmlFileName(subject: string | undefined, id: number) {
  return `${sanitizeFilename(subject || `conversation-${id}`)}.eml`;
}
